import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { STORAGE_KEYS } from '@/constants/storage';
import { setAnalyticsUserId, clearAnalyticsUserId } from '@/utils/analytics';

/**
 * ユーザープロフィール
 */
export interface User {
  id: string;          // OAuth user ID (Raksul user ID)
  email: string;
  name?: string;
  lastLoginAt?: string;
}

/**
 * 認証初期化の結果
 */
export interface AuthInitResult {
  success: boolean;
  error: string | null;
}

/**
 * 認証状態とアクション
 */
export interface AuthState {
  // State
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  initializeAuth: () => Promise<AuthInitResult>;
  logout: () => Promise<void>;
  clearError: () => void;
}

/**
 * API エンドポイントのベースURL
 */
// 開発環境のみ localhost フォールバックを使用
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ||
  (import.meta.env.DEV ? 'http://localhost:8080' : '');

/**
 * 認証初期化の進行中 Promise をキャッシュ
 * 複数の同時呼び出しが発生した場合に重複 API コールを防ぐ
 */
let initAuthPromise: Promise<AuthInitResult> | null = null;

/**
 * 認証ストア
 *
 * Zustand + persist middleware を使用してauth状態を管理
 * - user + isAuthenticated を localStorage に永続化
 * - app初期化時に localStorage から復元
 * - initializeAuth() でバックエンド検証
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true,
      error: null,

      /**
       * 認証状態を初期化（セッション検証）
       *
       * GET /api/v1/auth/profile を呼び出してユーザー情報を取得
       * - 成功時: user を設定して isAuthenticated = true
       * - 失敗時: エラーを設定して isAuthenticated = false
       * - Cookie で JWT が送信される
       * - 複数の同時呼び出しは同じ Promise を共有（race condition 防止）
       *
       * @returns 認証結果 { success: boolean, error: string | null }
       */
      initializeAuth: async () => {
        // 既に初期化中の場合は同じ Promise を返す
        if (initAuthPromise) {
          return initAuthPromise;
        }

        initAuthPromise = (async (): Promise<AuthInitResult> => {
          set({ isLoading: true, error: null });
          try {
            const response = await fetch(`${API_BASE_URL}/api/v1/auth/profile`, {
              method: 'GET',
              credentials: 'include', // HTTP-Only Cookie を含める
              headers: {
                'Content-Type': 'application/json',
              },
            });

            if (!response.ok) {
              throw new Error(`Failed to fetch user: ${response.statusText}`);
            }

            // レスポンスボディを取得
            const text = await response.text();

            // 未認証の場合、バックエンドは 200 + null を返す
            if (!text || text === 'null') {
              set({
                user: null,
                isAuthenticated: false,
                isLoading: false,
                error: null,
              });
              return { success: false, error: null };
            }

            const userData: User = JSON.parse(text);
            set({
              user: userData,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });
            // GA4 にユーザー ID を設定
            setAnalyticsUserId(userData.id);
            return { success: true, error: null };
          } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown error';
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              error: errorMessage,
            });
            return { success: false, error: errorMessage };
          } finally {
            // 完了後にキャッシュをクリア
            initAuthPromise = null;
          }
        })();

        return initAuthPromise;
      },

      /**
       * ログアウト
       *
       * - POST /api/v1/auth/logout を呼び出す
       * - バックエンドが HTTP-Only Cookie をクリア
       * - ローカルストレージもクリア
       */
      logout: async () => {
        set({ isLoading: true, error: null });
        try {
          await fetch(`${API_BASE_URL}/api/v1/auth/logout`, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
          });
        } catch (err) {
          console.error('Logout error:', err);
          // エラーの場合でも状態をクリア
        }

        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });

        // GA4 からユーザー ID をクリア
        clearAnalyticsUserId();

        // localStorage をクリア（persist middleware のクリア）
        localStorage.removeItem(STORAGE_KEYS.AUTH_STORAGE);
      },

      /**
       * エラーメッセージをクリア
       */
      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: STORAGE_KEYS.AUTH_STORAGE,
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
